import React, { useState, useEffect, useCallback } from "react";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, Container, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { v4 } from "uuid";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import ContentWrapper from "../../../components/content-wrapper";
import { ErrorField } from "../../shared/widgets/TranslationComponent/styles";
import { tickerHookFormValidation } from "../../shared/crud/form-validation";
import { InputToggle } from "../../bundle/components/InputToggle";
import {
  ScenarioEntity,
  ScenarioInput,
  ScenarioSchema,
  createEmptyScenario,
} from "./domain/scenario.entity";
import { ScenarioGrpcRepository } from "./infrastructure/repositories/ScenarioGrpcRepository";
import { useScenarioMutations } from "./application/hooks/useScenarioMutations";
import { AddActionModal } from "../shared/components/AddActionModal";
import { AddCriteriaModal } from "../shared/components/AddCriteriaModal";
import { CriteriaTable } from "../shared/components/CriteriaTable";
import { ActionsTable } from "../shared/components/ActionsTable";
import { isKnownActionKey } from "../shared/domain/globalAction.types";
import { useOccupations, useActionsManager, useCriteriaManager } from "../shared/hooks";
import { messages } from "./scenario.messages";
import { getAllEngineMultiConfigs } from "../../engineMultiConfig/infrastructure/engineMultiConfig.api";
import { EngineMultiConfigEntity } from "../../engineMultiConfig/domain/engineMultiConfig.entity";
import { WitLabel } from "../../shared/widgets/common";

const scenarioRepository = new ScenarioGrpcRepository();

function ScenarioEdit() {
  const intl = useIntl();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const propertyInfo = useProperty();
  const { createScenario, updateScenario } = useScenarioMutations(
    propertyInfo.ticker,
    scenarioRepository
  );
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [engineConfigs, setEngineConfigs] = useState<EngineMultiConfigEntity[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  const form = useForm<ScenarioInput, unknown, ScenarioEntity>({
    resolver: zodResolver(ScenarioSchema),
    defaultValues: createEmptyScenario(v4()),
  });

  const { occupations, loading: loadingOccupations } = useOccupations(propertyInfo.ticker);
  const actionsManager = useActionsManager(form, "globalActions");
  const criteriaManager = useCriteriaManager(form);

  useEffect(() => {
    const loadEngineConfigs = async () => {
      try {
        const configs = await getAllEngineMultiConfigs(propertyInfo.ticker, propertyInfo.token);
        setEngineConfigs(configs);
      } catch (error) {
        console.error("Error loading engine configs:", error);
      } finally {
        setLoadingConfigs(false);
      }
    };
    loadEngineConfigs();
  }, [propertyInfo.ticker, propertyInfo.token]);

  useEffect(() => {
    if (!isNew && id) {
      // Ignore late responses from a previous id/ticker so they cannot overwrite the form.
      let ignore = false;
      const loadScenario = async () => {
        try {
          const scenario = await scenarioRepository.findById(propertyInfo.ticker, id);
          if (!ignore && scenario) {
            form.reset(scenario);
          }
        } catch (error) {
          if (!ignore) console.error("Error loading Scenario:", error);
        } finally {
          if (!ignore) setLoading(false);
        }
      };
      loadScenario();
      return () => {
        ignore = true;
      };
    }
  }, [isNew, id, propertyInfo.ticker, propertyInfo.token, form]);

  const handleGoBack = useCallback(() => {
    history.push(`/${propertyInfo.ticker}/orchestrator/scenarios`);
  }, [history, propertyInfo.ticker]);

  const handleSubmit = useCallback(
    async (data: ScenarioEntity) => {
      try {
        setSaving(true);
        if (isNew) {
          await createScenario(data);
        } else {
          await updateScenario(data);
        }
        handleGoBack();
      } catch (error) {
        console.error("Error saving Scenario:", error);
      } finally {
        setSaving(false);
      }
    },
    [isNew, createScenario, updateScenario, handleGoBack]
  );

  if (loading || loadingOccupations || loadingConfigs) {
    return (
      <ContentWrapper>
        <SectionHeading>
          <FormattedMessage {...messages.title} />
        </SectionHeading>
        <Container className="p-4" fluid={true}>
          <Card className="p-3">
            <FormattedMessage {...messages.loading} />
          </Card>
        </Container>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Container className="p-4" fluid={true}>
        <Card className="p-3">
          <FormProvider {...form}>
            <Form>
              {/* Active Toggle */}
              <FormGroup>
                <FormLabel>
                  <FormattedMessage {...messages.active} />
                </FormLabel>
                <Controller
                  name="active"
                  control={form.control}
                  render={({ field }) => (
                    <InputToggle
                      className="witadmin-bs3"
                      disabled={saving}
                      {...field}
                      value={field.value ?? true}
                    />
                  )}
                />
              </FormGroup>

              {/* Ticker */}
              <FormGroup>
                <FormLabel htmlFor="tickerInput">
                  <FormattedMessage {...messages.ticker} />
                </FormLabel>
                <FormControl
                  id="tickerInput"
                  type="text"
                  className="!w-[200px]"
                  disabled={!isNew || saving}
                  {...form.register("ticker", tickerHookFormValidation(intl))}
                />
                {form.formState.errors["ticker"] && (
                  <ErrorField>{form.formState.errors["ticker"].message}</ErrorField>
                )}
              </FormGroup>

              {/* Name */}
              <FormGroup>
                <FormLabel htmlFor="nameInput">
                  <FormattedMessage {...messages.name} />
                </FormLabel>
                <FormControl
                  id="nameInput"
                  type="text"
                  className="!w-[200px]"
                  disabled={saving}
                  {...form.register("name")}
                />
                {form.formState.errors["name"] && (
                  <ErrorField>{form.formState.errors["name"].message}</ErrorField>
                )}
              </FormGroup>

              {/* Engine Configuration */}
              <FormGroup>
                <FormLabel>
                  <FormattedMessage {...messages.engineConfig} />
                </FormLabel>
                <Controller
                  name="engineMultiConfigId"
                  control={form.control}
                  render={({ field }) => (
                    <Form.Control
                      as="select"
                      className="!w-[300px]"
                      disabled={saving}
                      value={field.value || ""}
                      onChange={e => field.onChange(e.target.value)}>
                      <option value="">{intl.formatMessage(messages.defaultEngineConfig)}</option>
                      {engineConfigs.map(config => (
                        <option key={config.ticker} value={config.ticker}>
                          {config.ticker}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                />
              </FormGroup>

              {/* Conditions */}
              <FormGroup>
                <WitLabel>
                  <FormLabel>
                    <FormattedMessage {...messages.conditions} />
                  </FormLabel>
                </WitLabel>

                <CriteriaTable
                  conditions={criteriaManager.conditions}
                  onEdit={criteriaManager.handleEdit}
                  onDelete={criteriaManager.handleDelete}
                />

                <div>
                  <Button
                    variant="primary"
                    className="mt-3"
                    disabled={saving}
                    onClick={criteriaManager.handleOpenAdd}>
                    <FormattedMessage {...messages.addCondition} />
                  </Button>
                </div>
              </FormGroup>

              {/* Global Actions */}
              <FormGroup>
                <WitLabel>
                  <FormLabel>
                    <FormattedMessage {...messages.parameters} />
                  </FormLabel>
                </WitLabel>

                <ActionsTable
                  actions={actionsManager.actions}
                  onEdit={actionsManager.handleEdit}
                  onDelete={actionsManager.handleDelete}
                />

                <div>
                  <Button
                    variant="primary"
                    className="mt-3"
                    disabled={saving}
                    onClick={actionsManager.handleOpenAdd}>
                    <FormattedMessage {...messages.addParam} />
                  </Button>
                </div>
              </FormGroup>

              {/* Form Actions */}
              <div className="flex gap-4 mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving}
                  onClick={form.handleSubmit(handleSubmit)}>
                  {!saving && <FormattedMessage {...messages.save} />}
                  {saving && (
                    <>
                      <FormattedMessage {...messages.saving} />{" "}
                      <FontAwesomeIcon icon={faCircleNotch} spin />
                    </>
                  )}
                </Button>
                <Button variant="secondary" onClick={handleGoBack} disabled={saving}>
                  <FormattedMessage {...messages.back} />
                </Button>
              </div>
            </Form>
          </FormProvider>
        </Card>
      </Container>

      <AddCriteriaModal
        isOpen={criteriaManager.modalOpen}
        onClose={criteriaManager.handleClose}
        onSave={criteriaManager.handleSave}
        editingCondition={criteriaManager.editingCondition}
        editingIndex={criteriaManager.editingIndex ?? undefined}
        existingConditionTypes={criteriaManager.existingConditionTypes}
        occupations={occupations}
      />

      <AddActionModal
        isOpen={actionsManager.modalOpen}
        onClose={actionsManager.handleClose}
        onSave={actionsManager.handleSave}
        editingKey={
          actionsManager.editingKey && isKnownActionKey(actionsManager.editingKey)
            ? actionsManager.editingKey
            : undefined
        }
        editingValue={actionsManager.editingValue}
        existingKeys={actionsManager.existingKeys}
      />
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(ScenarioEdit);
